import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { approveBusiness } from '@/modules/Business/services'
import useAuthStore from '@/stores/useAuthStore'
import { ApprovalType, SingleApprovalProps, BulkApprovalProps } from './types'

interface UseBusinessApprovalOptions {
    mode: 'single' | 'bulk'
    type?: ApprovalType
    onSuccess: () => void
    onPartialSuccess?: () => void
}

export const useBusinessApproval = ({
    mode,
    type: _type,
    onSuccess,
    onPartialSuccess,
}: UseBusinessApprovalOptions) => {
    const { t } = useTranslation('business')
    const { employee } = useAuthStore()
    const [isBulkPending, setIsBulkPending] = useState(false)

    const { mutateAsync: approveMutation, isPending: isSinglePending } =
        useMutationWithHandler(
            { mutationFn: approveBusiness },
            {
                reactQueryKeys: QUERY_KEYS.BUSINESS,
                showSuccessToast: mode === 'single',
                successMessage: t('approved-success'),
            }
        )

    const isPending = mode === 'bulk' ? isBulkPending : isSinglePending

    // Single approve
    const handleSingleApprove = useCallback(
        async (props: SingleApprovalProps, feedback: string) => {
            const { businessID, employeeID, type: approvalType } = props

            // Check self-approve for leader
            if (
                approvalType === 'leader' &&
                employee?.employee_id === employeeID
            ) {
                return toast.error(t('approved-errors'))
            }

            if (!feedback.trim()) {
                return toast.error(t('blank-errors'))
            }

            try {
                const result = await approveMutation({
                    business_id: businessID,
                    business_feedback: feedback,
                })
                if (!result) return
                onSuccess()
            } catch (error) {
                console.error('Approval error:', error)
            }
        },
        [employee?.employee_id, t, approveMutation, onSuccess]
    )

    // Single reject
    const handleSingleReject = useCallback(
        async (props: SingleApprovalProps) => {
            const { businessID } = props

            try {
                const result = await approveMutation({
                    business_id: businessID,
                    business_feedback: 'rejected',
                })
                if (!result) return
                onSuccess()
            } catch (error) {
                console.error('Reject error:', error)
            }
        },
        [approveMutation, onSuccess]
    )

    // Bulk approve
    const handleBulkApprove = useCallback(
        async (props: BulkApprovalProps, feedback: string) => {
            const { ids } = props

            if (!feedback.trim()) {
                return toast.error(t('blank-errors'))
            }

            setIsBulkPending(true)
            try {
                const results = await Promise.allSettled(
                    ids.map((businessId) =>
                        approveBusiness({
                            business_id: businessId,
                            business_feedback: feedback,
                        }).then(() => businessId)
                    )
                )

                const successful = results
                    .filter(
                        (result): result is PromiseFulfilledResult<number> =>
                            result.status === 'fulfilled'
                    )
                    .map((result) => result.value)

                const failed = results.flatMap((result, index) =>
                    result.status === 'rejected' ? [ids[index]] : []
                )

                if (successful.length > 0) {
                    toast.success(
                        t('approved-success') + `: ${successful.join(', ')}`
                    )
                }

                if (failed.length > 0) {
                    toast.error(
                        t('approved-multiple-errors') + `: ${failed.join(', ')}`
                    )
                }

                if (failed.length === 0 && successful.length > 0) {
                    onSuccess()
                } else if (successful.length > 0) {
                    onPartialSuccess?.()
                }
            } catch (error) {
                console.error('Bulk approval error:', error)
            } finally {
                setIsBulkPending(false)
            }
        },
        [t, onSuccess, onPartialSuccess]
    )

    return {
        isPending,
        handleSingleApprove,
        handleSingleReject,
        handleBulkApprove,
    }
}
